import { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLocation } from 'react-router-dom';
import { useCity } from '@/contexts/CityContext';
import ReactMarkdown from 'react-markdown';

type Msg = { role: 'user' | 'assistant'; content: string };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/property-chat`;
const NUDGE_KEY = 'bv_booking_nudge_shown';

export function ChatBubble() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { city } = useCity();
  const hasGreeted = useRef(false);

  // One-time booking nudge on property pages
  const isPropertyPage = location.pathname.startsWith('/property/');

  useEffect(() => {
    if (isPropertyPage && !localStorage.getItem(NUDGE_KEY)) {
      const timer = setTimeout(() => {
        setShowNudge(true);
        localStorage.setItem(NUDGE_KEY, '1');
        // Auto-dismiss after 6s
        const dismiss = setTimeout(() => setShowNudge(false), 6000);
        return () => clearTimeout(dismiss);
      }, 2000);
      return () => clearTimeout(timer);
    } else if (!isPropertyPage) {
      setShowNudge(false);
    }
  }, [location.pathname]);

  // Auto-scroll
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  // Send initial greeting when chat opens for first time
  useEffect(() => {
    if (open && !hasGreeted.current && messages.length === 0) {
      hasGreeted.current = true;
      streamChat([{ role: 'user', content: `Hi, I'm browsing properties in ${city}. Say a brief hello and ask what area I'm looking in.` }], true);
    }
  }, [open]);

  const streamChat = useCallback(async (msgs: Msg[], isGreeting = false) => {
    setIsLoading(true);
    let assistantSoFar = '';

    const upsert = (chunk: string) => {
      assistantSoFar += chunk;
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant') {
          return prev.map((m, i) => i === prev.length - 1 ? { ...m, content: assistantSoFar } : m);
        }
        return [...prev, { role: 'assistant', content: assistantSoFar }];
      });
    };

    try {
      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: isGreeting ? msgs : msgs,
          city,
        }),
      });

      if (!resp.ok || !resp.body) {
        upsert("Sorry, I'm having trouble connecting. Please try again!");
        setIsLoading(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        let idx: number;
        while ((idx = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (!line.startsWith('data: ')) continue;
          const json = line.slice(6).trim();
          if (json === '[DONE]') break;
          try {
            const parsed = JSON.parse(json);
            const content = parsed.choices?.[0]?.delta?.content;
            if (content) upsert(content);
          } catch { /* partial */ }
        }
      }
    } catch {
      upsert("Sorry, something went wrong. Please try again!");
    }
    setIsLoading(false);
  }, [city]);

  const send = async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    setInput('');
    const userMsg: Msg = { role: 'user', content: text };
    const updated = [...messages, userMsg];
    setMessages(updated);
    await streamChat(updated);
  };

  return (
    <>
      {/* Booking nudge tooltip */}
      {showNudge && !open && (
        <div className="fixed bottom-20 right-4 z-50 max-w-[280px] animate-in slide-in-from-bottom-2 fade-in duration-300">
          <div className="bg-primary text-primary-foreground rounded-xl px-4 py-3 shadow-lg text-sm relative">
            <button
              onClick={() => setShowNudge(false)}
              className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-background text-foreground flex items-center justify-center text-xs shadow"
            >
              ×
            </button>
            <p className="font-medium">💡 Did you know?</p>
            <p className="mt-1 text-primary-foreground/90">
              You can book a viewing for this listing right now! Just click the <strong>Book a Viewing</strong> tab above — no phone calls needed.
            </p>
            <div className="absolute bottom-0 right-6 translate-y-1/2 rotate-45 w-3 h-3 bg-primary" />
          </div>
        </div>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-20 right-4 z-50 w-[340px] sm:w-[380px] h-[480px] bg-background border rounded-2xl shadow-2xl flex flex-col animate-in slide-in-from-bottom-4 fade-in duration-200">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b bg-primary rounded-t-2xl">
            <div className="flex items-center gap-2 text-primary-foreground">
              <MessageCircle className="w-5 h-5" />
              <span className="font-semibold text-sm">Property Assistant</span>
            </div>
            <button onClick={() => setOpen(false)} className="text-primary-foreground/80 hover:text-primary-foreground">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.filter(m => !(m.role === 'user' && m.content.includes("Say a brief hello"))).map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
                  msg.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-foreground'
                }`}>
                  {msg.role === 'assistant' ? (
                    <div className="prose prose-sm max-w-none [&>p]:m-0">
                      <ReactMarkdown>{msg.content}</ReactMarkdown>
                    </div>
                  ) : msg.content}
                </div>
              </div>
            ))}
            {isLoading && messages[messages.length - 1]?.role !== 'assistant' && (
              <div className="flex justify-start">
                <div className="bg-muted rounded-xl px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="p-3 border-t">
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Type a message..."
                className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                disabled={isLoading}
              />
              <Button type="submit" size="icon" disabled={isLoading || !input.trim()} className="shrink-0">
                <Send className="w-4 h-4" />
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Floating bubble button */}
      <button
        onClick={() => { setOpen(!open); setShowNudge(false); }}
        className="fixed bottom-4 right-4 z-50 w-14 h-14 rounded-full bg-primary text-primary-foreground shadow-lg hover:shadow-xl transition-all hover:scale-105 flex items-center justify-center"
        aria-label="Chat with us"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>
    </>
  );
}
